"use client";

import { useTranslation } from "@/src/i18n/client";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileUploadArea } from "./FileUploadArea";
import { AdvancedSettings } from "./AdvancedSettings";
import { TaskSelect } from "./TaskSelect";
import { AnalyzeButton } from "./AnalyzeButton";
import { FileHandlingSelect } from "./FileHandlingSelect";
import { fileToBase64 } from '@/src/lib/file';
import { toast } from "sonner";
import { Prompt } from '@/src/types/prompt.zod';
import { SessionStorageKey, SessionStorageModel, LocalStorageKey, LocalStorageModel, LastAnalysisSettings } from '@/storage.config';
import { createSafeStorage } from '@/src/lib/typedStorage';
import { Template } from "@/src/types/template.zod";
import { AiModel } from "@/src/types/ai.zod";
import { listPrompts } from "@/src/api/prompt";
import { listTemplates } from "@/src/api/template";
import { listModels } from "@/src/api/model";
import { clientLogger as logger } from "@/src/lib/clientLogger";
import { getSharedFileMetadata, getSharedFile, deleteSharedFile } from "@/src/lib/sharedFiles";
import { useNotifications } from "@/src/components/ui/notification";
import { uploadFilesForAnalysis } from '@/src/lib/directAnalysisUpload';

/**
 * Home page client component for GearLens.
 * Handles file selection, prompt/template/provider selection, and image analysis.
 *
 * - Allows users to upload a file and select analysis options.
 * - Calls the AI analysis endpoint and navigates to results on success.
 * - Stores analysis results and file content in sessionStorage.
 */
export default function AiClientForm({ onProcessingIdsChange }: { onProcessingIdsChange?: (ids: string[]) => void } = {}) {
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const sessionStorageService = useMemo(
    () => createSafeStorage<SessionStorageModel>(() => window.sessionStorage),
    []
  );
  const localStorageService = useMemo(
    () => createSafeStorage<LocalStorageModel>(() => window.localStorage),
    []
  );
  const handleAnalyzeRef = useRef<() => Promise<void>>(async () => {});
  const handledShareEventRef = useRef<string | null>(null);

  // Check for shared files from Web Share Target API
  const searchParams = useSearch({ strict: false }) as { shared?: string; shareError?: string };

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<"idle" | "uploading" | "analyzing">("idle");
  const [fileHandling, setFileHandling] = useState<"single" | "multiple">("multiple");

  // Check if any files are PDFs or TIFFs (can't be combined into single document)
  const hasPdfOrTiff = selectedFiles.some((f) => {
    const t = f.type.toLowerCase();
    const n = f.name.toLowerCase();
    return t === 'application/pdf' || t === 'image/tiff' || t === 'image/tif'
      || n.endsWith('.pdf') || n.endsWith('.tiff') || n.endsWith('.tif');
  });
  const canCombine = selectedFiles.length > 1 && !hasPdfOrTiff;

  // Providers
  const [clientProviders, setClientProviders] = useState<AiModel[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>("");

  // Prompts
  const [clientPrompts, setClientPrompts] = useState<Prompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<string>("");

  // Templates
  const [clientTemplates, setClientTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");

  // Fetch prompts using TanStack Query
  const { data: promptsData, error: promptsError, isLoading: promptsLoading } = useQuery({
    queryKey: ['prompts'],
    queryFn: () => listPrompts({ data: {} }),
  });

  // Fetch templates using TanStack Query
  const { data: templatesData, error: templatesError, isLoading: templatesLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => listTemplates({ data: {} }),
  });

  // Fetch models using TanStack Query
  const { data: modelsData, error: modelsError, isLoading: modelsLoading } = useQuery({
    queryKey: ['models'],
    queryFn: () => listModels({ data: {} }),
  });

  const analyzeMutation = useMutation({
    mutationFn: (data: { files: File[]; promptId: string; aiModelId: string; storeData: boolean; fileHandling: "single" | "multiple" }) =>
      uploadFilesForAnalysis(data),
  });

  // Prompts logic
  useEffect(() => {
    if (promptsData) {
      setClientPrompts(promptsData as Prompt[]);
      if (promptsData.length > 0 && promptsData[0]?.id && !selectedPrompt) {
        setSelectedPrompt(promptsData[0].id);
      }
    }
    if (promptsError) {
      logger.error({ error: promptsError }, "Failed to fetch prompts");
    }
  }, [promptsData, promptsError, selectedPrompt]);

  // Templates logic
  useEffect(() => {
    if (templatesData) {
      setClientTemplates(templatesData as Template[]);
      if (templatesData.length > 0 && templatesData[0]?.id && !selectedTemplate) {
        setSelectedTemplate(templatesData[0].id);
      }
    }
    if (templatesError) {
      logger.error({ error: templatesError }, "Failed to fetch templates");
    }
  }, [templatesData, templatesError, selectedTemplate]);

  // Models logic
  useEffect(() => {
    if (modelsData) {
      setClientProviders(modelsData as AiModel[]);
      if (modelsData.length > 0 && modelsData[0]?.id && !selectedProvider) {
        setSelectedProvider(modelsData[0].id);
      }
    }
    if (modelsError) {
      logger.error({ error: modelsError }, "Failed to fetch AI models");
    }
  }, [modelsData, modelsError, selectedProvider]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  /**
   * Handles the analysis of the selected files using the selected prompt and provider.
   * Stores the result and file content in sessionStorage and navigates to the results page.
   */
  const handleAnalyze = useCallback(async () => {
    if (!selectedFiles.length) {
      toast.error(t('homePage.error.selectFileFirst'));
      return;
    }
    if (!selectedTemplate) {
      toast.error(t('homePage.error.selectTemplateFirst'));
      return;
    }
    setStatus("uploading");
    try {
      const filesPayload: { name: string, type: string, data: string }[] = [];
      for (const file of selectedFiles) {
        const base64Data = await fileToBase64(file);
        filesPayload.push({
          name: file.name,
          type: file.type,
          data: base64Data
        });
      }
      const analysisStartedAt = new Date().toISOString();
      
      // Save settings for future auto-analysis
      const settings: LastAnalysisSettings = {
        templateId: selectedTemplate,
        modelId: selectedProvider,
        fileHandling: fileHandling,
        timestamp: Date.now(),
      };
      localStorageService.set(LocalStorageKey.LastAnalysisSettings, settings);
      
      setStatus("analyzing");
      const { result } = await analyzeMutation.mutateAsync({
        files: selectedFiles,
        promptId: selectedPrompt,
        aiModelId: selectedProvider,
        storeData: true,
        fileHandling: fileHandling,
      });
      
      // Store files for results page viewer
      if (filesPayload) sessionStorageService.set(SessionStorageKey.DrawingFiles, filesPayload);
      
      // Handle response - extract drawing IDs for tracking
      if (result && (result as any).data) {
        const responseData = (result as any).data;
        let drawingIds: string[] = [];
        
        // For multiple files, we get an array of {drawingId, fileId, fileName}
        if (Array.isArray(responseData)) {
          drawingIds = responseData.map((d: any) => d.drawingId);
          sessionStorageService.set(
            SessionStorageKey.PendingDrawings,
            responseData.map((item: any) => ({
              ...item,
              startedAt: analysisStartedAt,
            }))
          );
          sessionStorageService.remove(SessionStorageKey.DrawingData);
        } else if (responseData.drawingId) {
          // Single drawing
          drawingIds = [responseData.drawingId];
          sessionStorageService.set(SessionStorageKey.PendingDrawings, [{
            drawingId: responseData.drawingId,
            fileId: '',
            fileName: filesPayload[0]?.name || '',
            startedAt: analysisStartedAt,
          }]);
          sessionStorageService.remove(SessionStorageKey.DrawingData);
        } else {
          // Legacy: complete drawing data returned directly
          sessionStorageService.set(SessionStorageKey.DrawingData, responseData);
          sessionStorageService.remove(SessionStorageKey.PendingDrawings);
        }
        
        // Track processing IDs for JobHistory (shows shimmer until processed)
        onProcessingIdsChange?.(drawingIds);
        
        // Invalidate the recent drawings cache so JobHistory refreshes
        queryClient.invalidateQueries({ queryKey: ['recentDrawings'] });
        
        // Show started notification — analysis runs asynchronously in the background
        const fileCount = selectedFiles.length;
        const fileName = selectedFiles[0]?.name || 'file';
        addNotification({
          type: 'info',
          title: 'Analysis started',
          message: fileCount === 1
            ? `"${fileName}" uploaded — analysis is running in the background. Results will appear on the Results page when ready.`
            : `${fileCount} files uploaded — analysis started. Results will appear on the Results page when ready.`,
          duration: 7000,
          action: {
            label: 'View Progress',
            onClick: () => {
              navigate({ to: '/results' });
            },
          },
        });
      }
      navigate({ to: '/results' });
    } catch (err) {
      let errorMessage = t('homePage.error.unknownAnalysisError');
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err && 'message' in err && typeof (err as { message?: unknown }).message === 'string') {
        errorMessage = (err as { message: string }).message;
      }
      toast.error(`Analysis Error: ${errorMessage}`);
    } finally {
      setStatus("idle");
    }
  }, [
    selectedFiles,
    t,
    selectedTemplate,
    localStorageService,
    selectedProvider,
    fileHandling,
    analyzeMutation,
    selectedPrompt,
    sessionStorageService,
    onProcessingIdsChange,
    queryClient,
    addNotification,
    navigate,
  ]);

  useEffect(() => {
    handleAnalyzeRef.current = handleAnalyze;
  }, [handleAnalyze]);

  // Check for shared files from Web Share Target API
  useEffect(() => {
    if (!isMounted) return;

    const sharedTimestamp = searchParams?.shared;
    const shareError = searchParams?.shareError;
    const shareEventKey = sharedTimestamp
      ? `shared:${sharedTimestamp}`
      : shareError
        ? `error:${shareError}`
        : null;

    if (!shareEventKey || handledShareEventRef.current === shareEventKey) {
      return;
    }

    if (sharedTimestamp && (templatesLoading || modelsLoading)) {
      return;
    }

    handledShareEventRef.current = shareEventKey;

    const loadSharedFile = async () => {
      if (sharedTimestamp) {
        try {
          const metadata = await getSharedFileMetadata(sharedTimestamp);

          if (metadata) {
            const file = await getSharedFile(metadata);

            if (file) {
              setSelectedFiles([file]);

              // Clean up the shared file from cache
              await deleteSharedFile(metadata);

              // Check if we have saved settings for auto-analysis
              const savedSettings = localStorageService.get(LocalStorageKey.LastAnalysisSettings);

              if (savedSettings && savedSettings.templateId && savedSettings.modelId) {
                // Verify the settings are still valid (template and model exist)
                const templateExists = clientTemplates.some(t => t.id === savedSettings.templateId);
                const modelExists = clientProviders.some(p => p.id === savedSettings.modelId);

                if (templateExists && modelExists) {
                  // Auto-analyze with saved settings
                  addNotification({
                    type: 'info',
                    title: 'Analyzing in background',
                    message: `"${file.name}" is being analyzed automatically...`,
                    duration: 3000,
                  });

                  // Set the saved settings
                  setSelectedTemplate(savedSettings.templateId);
                  setSelectedProvider(savedSettings.modelId);
                  setFileHandling(savedSettings.fileHandling);

                  // Trigger analysis after a short delay to ensure state is updated
                  setTimeout(() => {
                    void handleAnalyzeRef.current();
                  }, 500);
                } else {
                  // Settings are outdated
                  addNotification({
                    type: 'success',
                    title: 'File loaded!',
                    message: `"${file.name}" has been loaded. Your previous settings are no longer available.`,
                    duration: 5000,
                  });
                }
              } else {
                // No saved settings - just show success
                addNotification({
                  type: 'success',
                  title: 'File loaded!',
                  message: `"${file.name}" has been loaded from share. Select your settings and analyze.`,
                  duration: 5000,
                });
              }
            } else {
              console.warn('Shared file metadata found but file could not be retrieved');
            }
          } else {
            console.warn('Shared file metadata not found for timestamp:', sharedTimestamp);
          }
        } catch (error) {
          console.error('Error loading shared file:', error);
          addNotification({
            type: 'error',
            title: 'Failed to load shared file',
            message: 'Please select your file manually.',
          });
        }
      } else if (shareError) {
        addNotification({
          type: 'error',
          title: 'Share failed',
          message: 'There was an error receiving the shared file.',
        });
      }
    };

    void loadSharedFile();
  }, [
    isMounted,
    searchParams?.shared,
    searchParams?.shareError,
    templatesLoading,
    modelsLoading,
    addNotification,
    clientTemplates,
    clientProviders,
    localStorageService,
  ]);

  if (!isMounted) {
    return null;
  }

  /**
   * Handles file selection from the FileUploadArea component.
   * @param files Array of selected File objects
   */
  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(files);
    // Auto-switch to batch when PDFs/TIFFs are present
    const anyDoc = files.some((f) => {
      const t = f.type.toLowerCase();
      const n = f.name.toLowerCase();
      return t === 'application/pdf' || t === 'image/tiff' || t === 'image/tif'
        || n.endsWith('.pdf') || n.endsWith('.tiff') || n.endsWith('.tif');
    });
    if (anyDoc && files.length > 1) {
      setFileHandling('multiple');
    }
  };

  return (
    <main className="flex grow flex-col items-center justify-center">
      <div className="w-full">
        <FileUploadArea onFilesChanged={handleFilesSelected} disabled={status !== "idle"} />
        <div className="mt-4">
          <TaskSelect
            value={selectedPrompt}
            onValueChange={setSelectedPrompt}
            tasks={clientPrompts}
            isLoading={promptsLoading}
          />
        </div>
        {canCombine && (
          <div className="mt-4">
            <FileHandlingSelect
              value={fileHandling}
              onValueChange={setFileHandling}
              canCombine={canCombine}
              hasPdfOrTiff={hasPdfOrTiff}
              fileCount={selectedFiles.length}
            />
          </div>
        )}
        <AdvancedSettings
          selectedTemplate={selectedTemplate}
          onTemplateChange={setSelectedTemplate}
          availableTemplates={clientTemplates}
          templatesLoading={templatesLoading}
          selectedProvider={selectedProvider}
          onProviderChange={setSelectedProvider}
          availableProviders={clientProviders}
        />
        <AnalyzeButton
          onClick={handleAnalyze}
          disabled={
            !selectedFiles.length ||
            promptsLoading ||
            templatesLoading ||
            !selectedPrompt ||
            !selectedTemplate
          }
          uploading={status === "uploading"}
          analyzing={status === "analyzing"}
        />
      </div>
    </main>
  );
}
